import { useEffect, useState } from "react";
import { Loader2, Quote, Search, Sparkles, Trash2, X } from "lucide-react";

const ReportEditSelectionModal = ({
  selection,
  editSuggestion,
  isPreviewingEdit = false,
  isApplyingEdit = false,
  isRejectingEdit = false,
  error,
  onPreviewEdit,
  onDeleteSelection,
  onApplyEdit,
  onRejectEdit,
  onClearSelection,
  onSourceAwareEdit,
}) => {
  const [editInstruction, setEditInstruction] = useState("");

  useEffect(() => {
    setEditInstruction("");
  }, [selection?.text]);

  if (!selection && !editSuggestion) return null;

  const isBusy = isPreviewingEdit || isApplyingEdit || isRejectingEdit;
  const canPreview =
    selection?.mapped !== false && editInstruction.trim().length > 0 && !isBusy;
  const canDelete = selection?.mapped !== false && !isBusy;
  const canUseSelectedText = Boolean(selection?.text) && !isBusy;
  const previewQuickAction = (instruction) => {
    setEditInstruction(instruction);
    onPreviewEdit?.(instruction);
  };

  return (
    <div data-report-ui="true" className="absolute right-6 top-16 z-30 w-[min(460px,calc(100%-3rem))] rounded-xl border border-surfaceBorder bg-surface/95 p-3 shadow-login-panel backdrop-blur-md">
      <div className="mb-3 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-sm font-medium text-mainText">
            Selected {selection?.block_type || "text"}
          </div>
          <p className="mt-1 text-xs text-mutedText">
            Preview a precise edit before applying it to the report.
          </p>
        </div>
        <button
          type="button"
          onClick={onClearSelection}
          disabled={isBusy}
          className="rounded-lg border border-transparent p-1.5 text-mutedText transition hover:border-velBlueSecond/25 hover:bg-btnHover hover:text-mainText disabled:cursor-not-allowed disabled:opacity-50"
          aria-label="Close selected edit"
          title="Close"
        >
          <X className="h-4 w-4" strokeWidth={1.7} />
        </button>
      </div>

      {error && (
        <p className="mb-3 rounded-lg border border-logoutColor/20 bg-logoutColor/10 px-2 py-1.5 text-[11px] leading-relaxed text-logoutColor">
          {error}
        </p>
      )}

      {selection?.text && (
        <div className="mb-3">
          <div className="mb-1 text-[11px] font-medium uppercase tracking-wide text-mutedText">
            Original
          </div>
          <div className="max-h-28 overflow-y-auto rounded-lg border border-surfaceBorder bg-mainBg/45 p-2 text-[12px] leading-relaxed text-mainText">
            {selection.display_text || selection.text}
          </div>
          {selection.mapped === false && (
            <p className="mt-2 rounded-lg border border-yellow-500/20 bg-yellow-500/10 px-2 py-1.5 text-[11px] leading-relaxed text-yellow-500">
              {selection.unmapped_reason ||
                "This selection could not be mapped safely. Use the paragraph/section ⋮ handle or select a clearer plain-text passage."}
            </p>
          )}
        </div>
      )}

      {editSuggestion ? (
        <div className="space-y-3">
          <div>
            <div className="mb-1 text-[11px] font-medium uppercase tracking-wide text-mutedText">
              Suggested replacement
            </div>
            <div className="max-h-52 overflow-y-auto whitespace-pre-wrap rounded-lg border border-velBlue/20 bg-mainBg/45 p-2 text-[12px] leading-relaxed text-mainText">
              {editSuggestion.replacement_text || "Delete selected text."}
            </div>
          </div>
          {editSuggestion.summary && (
            <p className="text-[11px] leading-relaxed text-mutedText">
              {editSuggestion.summary}
            </p>
          )}
          {Array.isArray(editSuggestion.sources) && editSuggestion.sources.length > 0 && (
            <details className="rounded-lg border border-surfaceBorder bg-mainBg/35 px-2.5 py-2 text-[11px] text-mutedText">
              <summary className="cursor-pointer font-medium text-mainText">
                Sources used ({editSuggestion.sources.length})
              </summary>
              <ol className="mt-2 space-y-1.5">
                {editSuggestion.sources.slice(0, 6).map((source) => (
                  <li key={`${source.index}-${source.url || source.title}`} className="grid grid-cols-[28px_minmax(0,1fr)] gap-1.5">
                    <span className="font-mono text-mutedText/75">[{source.index}]</span>
                    <span className="min-w-0">
                      <span className="block truncate text-mainText/90">{source.title || source.url || `Source ${source.index}`}</span>
                      {source.url && <span className="block truncate text-mutedText/75">{source.url}</span>}
                    </span>
                  </li>
                ))}
              </ol>
            </details>
          )}
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onApplyEdit}
              disabled={isApplyingEdit || isRejectingEdit}
              className="inline-flex items-center gap-1.5 rounded-lg border border-velBlue/30 bg-velBlue/15 px-3 py-1.5 text-[11px] text-velBlue transition hover:bg-velBlue/20 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isApplyingEdit && <Loader2 className="h-3 w-3 animate-spin" />}
              Apply
            </button>
            <button
              type="button"
              onClick={onRejectEdit}
              disabled={isApplyingEdit || isRejectingEdit}
              className="inline-flex items-center gap-1.5 rounded-lg border border-surfaceBorder px-3 py-1.5 text-[11px] text-mutedText transition hover:border-logoutColor/25 hover:bg-logoutColor/10 hover:text-logoutColor disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isRejectingEdit && <Loader2 className="h-3 w-3 animate-spin" />}
              Reject
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          <div>
            <div className="mb-1.5 text-[11px] font-medium uppercase tracking-wide text-mutedText">
              Quick actions
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() =>
                  previewQuickAction(
                    "Rewrite the selected text to be clearer and easier to read while preserving facts, tone, markdown, and citations.",
                  )
                }
                disabled={!canDelete}
                className="inline-flex items-center gap-1.5 rounded-lg border border-surfaceBorder px-2.5 py-1.5 text-[11px] text-mutedText transition hover:border-velBlueSecond/25 hover:bg-btnHover hover:text-mainText disabled:cursor-not-allowed disabled:opacity-50"
              >
                <Sparkles className="h-3 w-3" strokeWidth={1.8} />
                Clarify
              </button>
              <button
                type="button"
                onClick={() =>
                  previewQuickAction(
                    "Make the selected text more concise while preserving the important facts, citations, and markdown structure.",
                  )
                }
                disabled={!canDelete}
                className="inline-flex items-center gap-1.5 rounded-lg border border-surfaceBorder px-2.5 py-1.5 text-[11px] text-mutedText transition hover:border-velBlueSecond/25 hover:bg-btnHover hover:text-mainText disabled:cursor-not-allowed disabled:opacity-50"
              >
                Tighten
              </button>
              <button
                type="button"
                onClick={() => onSourceAwareEdit?.("sources")}
                disabled={!canUseSelectedText || !onSourceAwareEdit}
                className="inline-flex items-center gap-1.5 rounded-lg border border-surfaceBorder px-2.5 py-1.5 text-[11px] text-mutedText transition hover:border-velBlueSecond/25 hover:bg-btnHover hover:text-mainText disabled:cursor-not-allowed disabled:opacity-50"
              >
                <Search className="h-3 w-3" strokeWidth={1.8} />
                Find more sources
              </button>
              <button
                type="button"
                onClick={() => onSourceAwareEdit?.("citation")}
                disabled={!canUseSelectedText || !onSourceAwareEdit}
                className="inline-flex items-center gap-1.5 rounded-lg border border-surfaceBorder px-2.5 py-1.5 text-[11px] text-mutedText transition hover:border-velBlueSecond/25 hover:bg-btnHover hover:text-mainText disabled:cursor-not-allowed disabled:opacity-50"
              >
                <Quote className="h-3 w-3" strokeWidth={1.8} />
                Improve citations
              </button>
            </div>
          </div>

          <textarea
            value={editInstruction}
            onChange={(event) => setEditInstruction(event.target.value)}
            rows={3}
            placeholder="Tell the agent how to rewrite the selected text…"
            className="w-full resize-none rounded-lg border border-surfaceBorder bg-mainBg/45 px-3 py-2 text-[12.5px] leading-relaxed text-mainText outline-none placeholder:text-mutedText/70 focus:border-velBlue/40"
          />
          <div className="flex flex-wrap items-center justify-between gap-2">
            <button
              type="button"
              onClick={onDeleteSelection}
              disabled={!canDelete}
              className="inline-flex items-center gap-1.5 rounded-lg border border-logoutColor/30 bg-logoutColor/10 px-3 py-1.5 text-[11px] font-medium text-logoutColor transition hover:bg-logoutColor/15 disabled:cursor-not-allowed disabled:border-surfaceBorder disabled:bg-transparent disabled:text-mutedText/50"
            >
              <Trash2 className="h-3 w-3" strokeWidth={1.8} />
              Delete selection
            </button>
            <button
              type="button"
              onClick={() => onPreviewEdit?.(editInstruction.trim())}
              disabled={!canPreview}
              className="inline-flex items-center gap-1.5 rounded-lg border border-velBlue/30 bg-velBlue/15 px-3 py-1.5 text-[11px] text-velBlue transition hover:bg-velBlue/20 disabled:cursor-not-allowed disabled:border-surfaceBorder disabled:bg-transparent disabled:text-mutedText/50"
            >
              {isPreviewingEdit && <Loader2 className="h-3 w-3 animate-spin" />}
              Preview edit
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReportEditSelectionModal;
